import { useState, useCallback } from "react";
import { KeyRound, Eye, EyeOff, X, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { changePassword } from "../api/auth";

/**
 * useChangePassword
 * Same drop-in pattern as useConfirm() / useSaveOptions().
 * Usage:
 *   const { openChangePassword, changePasswordState, closeChangePassword } = useChangePassword();
 *   <button onClick={openChangePassword}>Change Password</button>
 *
 * Render <ChangePasswordDialog changePasswordState={changePasswordState} onClose={closeChangePassword} />
 * once, near the top of the component's returned JSX.
 */
export function useChangePassword() {
  const [changePasswordState, setChangePasswordState] = useState(null); // {} when open, null when closed

  const openChangePassword = useCallback(() => {
    setChangePasswordState({});
  }, []);

  const closeChangePassword = useCallback(() => {
    setChangePasswordState(null);
  }, []);

  return { openChangePassword, changePasswordState, closeChangePassword };
}

const initialForm = { oldPassword: "", newPassword: "", confirmPassword: "" };
const initialShow = { old: false, next: false, confirm: false };

/**
 * ChangePasswordDialog - centered modal with old/new/confirm password
 * inputs. Verifies the old password against the account's current one
 * server-side before updating.
 */
export default function ChangePasswordDialog({ changePasswordState, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [show, setShow] = useState(initialShow);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  if (!changePasswordState) return null;

  const resetAndClose = () => {
    setForm(initialForm);
    setShow(initialShow);
    setErrors({});
    setApiError("");
    setSuccess("");
    setLoading(false);
    onClose();
  };

  const validate = () => {
    const temp = {};
    if (!form.oldPassword) temp.oldPassword = "Old password is required";
    if (!form.newPassword) {
      temp.newPassword = "New password is required";
    } else if (form.newPassword.length < 6) {
      temp.newPassword = "Must be at least 6 characters";
    }
    if (!form.confirmPassword) {
      temp.confirmPassword = "Please confirm your new password";
    } else if (form.newPassword && form.confirmPassword !== form.newPassword) {
      temp.confirmPassword = "Passwords do not match";
    }
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setSuccess("");
    if (!validate()) return;

    try {
      setLoading(true);
      await changePassword({
        old_password: form.oldPassword,
        new_password: form.newPassword,
        confirm_password: form.confirmPassword,
      });
      setSuccess("Password changed successfully.");
      setForm(initialForm);
      setTimeout(resetAndClose, 1200);
    } catch (error) {
      setApiError(
        error.response?.data?.message || "Unable to change password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: "oldPassword", showKey: "old", label: "Old Password", placeholder: "Enter current password" },
    { key: "newPassword", showKey: "next", label: "New Password", placeholder: "Enter new password" },
    { key: "confirmPassword", showKey: "confirm", label: "Confirm New Password", placeholder: "Re-enter new password" },
  ];

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0 w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
              <KeyRound size={18} className="text-blue-600" />
            </div>
            <p className="text-sm font-bold text-slate-700">Change Password</p>
          </div>
          <button
            onClick={resetAndClose}
            aria-label="Close"
            className="shrink-0 text-slate-400 hover:text-slate-600 transition"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3.5">
          {apiError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-xs font-medium">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <span>{apiError}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-lg text-xs font-medium">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {fields.map(({ key, showKey, label, placeholder }) => (
            <div key={key}>
              <label className="block mb-1.5 text-xs font-semibold text-slate-600">{label}</label>
              <div
                className={`relative rounded-lg border transition-all ${
                  errors[key]
                    ? "border-red-300 ring-2 ring-red-100"
                    : "border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100"
                }`}
              >
                <input
                  type={show[showKey] ? "text" : "password"}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full pl-3 pr-10 py-2.5 rounded-lg outline-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={() => setShow({ ...show, [showKey]: !show[showKey] })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                >
                  {show[showKey] ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
            </div>
          ))}

          <div className="pt-1 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Changing..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}